import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "../lib/api";
import { realtime } from "../lib/ws";

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; g.gain.value = 0.04;
    o.start(); o.stop(ctx.currentTime + 0.15);
  } catch {}
}

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "ahora";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
}

export const NotificationBell = ({ mobile = false }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const prevUnread = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
      if (prevUnread.current !== null && data.unread > prevUnread.current && localStorage.getItem("notifSound") !== "off") beep();
      prevUnread.current = data.unread;
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const off = realtime.on((msg) => { if (msg.type === "notification") load(); });
    return () => off();
  }, [load]);

  const readAll = async () => { await api.put("/notifications/read-all"); load(); };

  const openItem = (n) => {
    if (!n.read) api.put(`/notifications/${n.id}/read`).then(load).catch(() => {});
    setOpen(false);
    if (n.board_id) navigate(`/boards/${n.board_id}`);
  };

  return (
    <div className={mobile ? "relative" : "relative px-3 pb-1"}>
      {mobile ? (
        <button onClick={() => setOpen((o) => !o)} data-testid="notifications-bell-mobile"
          className={`w-full h-full flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${open ? "text-primary" : "text-zinc-400"}`}>
          <span className="relative">
            <Bell className={`h-5 w-5 ${unread > 0 ? "animate-bell" : ""}`} />
            {unread > 0 && (
              <span data-testid="unread-badge-mobile" className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </span>
          Alertas
        </button>
      ) : (
      <button onClick={() => setOpen((o) => !o)} data-testid="notifications-bell"
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-400 hover:text-white hover:bg-white/5 transition-colors group">
        <span className="relative">
          <Bell className={`h-5 w-5 group-hover:rotate-12 transition-transform duration-200 ${unread > 0 ? "animate-bell" : ""}`} />
          {unread > 0 && (
            <span data-testid="unread-badge" className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </span>
        <span className="hidden lg:block">Notificaciones</span>
      </button>
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notifications-panel" className={`fixed left-2 right-2 sm:right-auto z-50 sm:w-[360px] max-h-[70vh] overflow-y-auto bg-[#121214] border border-white/10 rounded-2xl shadow-2xl animate-fade-up ${mobile ? "bottom-20 sm:left-2" : "bottom-16 sm:left-[76px] lg:left-[260px]"}`}>
            <div className="sticky top-0 bg-[#121214] flex items-center justify-between px-4 py-3 border-b border-white/10 z-10">
              <h3 className="font-display font-bold text-sm">Notificaciones</h3>
              <button onClick={readAll} data-testid="mark-all-read" className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
                <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
              </button>
            </div>
            {items.length === 0 && <p className="text-sm text-zinc-600 p-6 text-center">No tienes notificaciones.</p>}
            {items.map((n, i) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`}
                style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}
                className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors animate-fade-up ${!n.read ? "bg-primary/5" : ""}`}>
                <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ background: n.read ? "#3F3F46" : n.actor_color }} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-zinc-300"><span className="font-semibold text-white">{n.actor_name}</span> {n.text}</span>
                  <span className="block text-xs text-zinc-600 mt-0.5">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
